// import lib(s)
import React, { useEffect, useState, useCallback, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./UploadTask.module.css"
import { useTheme } from "@emotion/react"

import { useAxiosPrivate, useDocumentTitle } from "../../hooks"
import { PAGE_TITLE } from "../../constants/constants"
import { METHODS, errorHandler } from "../../hooks/useFetch"


import Layout from "../../layouts/common/Layout"
import PlaceholderText from "../../components/common/SampleText"
import Loader from "../../components/common/Loader"
import toast from "react-hot-toast"

import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Box from "@mui/material/Box"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import FormLabel from "@mui/material/FormLabel"
import FormControl from "@mui/material/FormControl"
import FormGroup from "@mui/material/FormGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Checkbox from "@mui/material/Checkbox"
import Switch from "@mui/material/Switch"
import Slider from "@mui/material/Slider"
import Link from "@mui/material/Link"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import ListSubheader from '@mui/material/ListSubheader';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import LinearProgress from "@mui/material/LinearProgress"
import Chip from "@mui/material/Chip"
import { Grid } from "@mui/material"

import { MuiColorInput } from 'mui-color-input'

import AddIcon from '@mui/icons-material/Add';
import BackspaceIcon from '@mui/icons-material/Backspace';

import { useDropzone } from "react-dropzone"

import atc2json from "../../utils/atc2json"
import universalFormatter, { resolveEcgType, unwrapRecordingEnvelope } from "../../utils/universalFormatter"

import copy2cliboard from "../../utils/copy2cliboard"

const UploadTask = () => {

	const DEFAULT_BEAT = { name: "Normal", color: "#6CCA6E", slug: "n" }
	const DEFAULT_INTERVAL = { name: "Noise", color: "#8c8c8c", slug: "no" }

	useDocumentTitle(PAGE_TITLE.UPLOAD_TASK)
	const dispatch = useDispatch()
	const CurrentUserEmail = useSelector((state) => state.auth.user.email)
	const theme = useTheme()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [Files, setFiles] = useState({})
	const [GlobalAnn, setGlobalAnn] = useState([])
	const [NewGlobalAnn, setNewGlobalAnn] = useState("")
	const fileInputRef = useRef(null)
	const [Beats, setBeats] = useState([])
	const [NewBeat, setNewBeat] = useState({name: "", color: "", slug: ""})
	const [Intervals, setIntervals] = useState([])
	const [NewInterval, setNewInterval] = useState({name: "", color: "", slug: ""})
	const [TaskName, setTaskName] = useState("")
	const [TaskInstructions, setTaskInstructions] = useState("")
	const [TaskType, setTaskType] = useState(1)
	const [DisableBeatAnnotation, setDisableBeatAnnotation] = useState(false)
	const [DisableIntervalAnnotation, setDisableIntervalAnnotation] = useState(false)
	const [DisableMedianBeatFiducialPointAnnotation, setDisableMedianBeatFiducialPointAnnotation] = useState(false)
	const [HideJsonView, setHideJsonView] = useState(false)
	const [DisableRmsView, setDisableRmsView] = useState(false)
	const [HideAlgorithmAnalysis, setHideAlgorithmAnalysis] = useState(false)
	const [RequireDoctorNote, setRequireDoctorNote] = useState(false)
	const [RequireGlobalAnnotation, setRequireGlobalAnnotation] = useState(false)
	const [EnableGlobalAnnotationMutex, setEnableGlobalAnnotationMutex] = useState(false)
	const [GlobalMutexGroups, setGlobalMutexGroups] = useState([])
	const [NewMutexGroup, setNewMutexGroup] = useState("")
	const [GlobalAnnotationGroupAssignments, setGlobalAnnotationGroupAssignments] = useState({})
	const [disabled, setDisabled] = useState(false)
	const [uploadProgress, setUploadProgress] = useState({ total: 0, completed: 0, failed: 0 })
	const abortControllerRef = useRef(null)

	const addGlobalAnn = (e) => {
		if (NewGlobalAnn === "") {
			toast.error("Global Annotation cannot be empty")
			return
		}
		const annArray = NewGlobalAnn.split(",").map(a => a.trim())
		// remove empty string from array
		const filtered = annArray.filter(function (el) {
			return el != "";
		});

		const { added, skipped } = addAnnotationsToSet(filtered, GlobalAnn, setGlobalAnn)
		if (added === 0) {
			toast.error("All annotations already exist")
		} else {
			toast.success(`Added ${added} global annotation(s)${skipped > 0 ? ` (${skipped} duplicate${skipped > 1 ? 's' : ''} skipped)` : ''}`)
		}
		setNewGlobalAnn("")
	}

	const addAnnotationsToSet = (annotations, currentList, setFn) => {
		const seen = new Set(currentList.map(a => a.toLowerCase()))
		const newAnnotations = []
		annotations.forEach(ann => {
			const trimmed = ann.trim()
			if (trimmed && !seen.has(trimmed.toLowerCase())) {
				seen.add(trimmed.toLowerCase())
				newAnnotations.push(trimmed)
			}
		})
		setFn([...currentList, ...newAnnotations])
		return { added: newAnnotations.length, skipped: annotations.length - newAnnotations.length }
	}

	const addMutexGroup = () => {
		const name = NewMutexGroup.trim()
		if (!name) {
			toast.error("Mutex group name cannot be empty")
			return
		}
		if (GlobalMutexGroups.some((group) => group.toLowerCase() === name.toLowerCase())) {
			toast.error("Mutex group already exists")
			return
		}
		setGlobalMutexGroups([...GlobalMutexGroups, name])
		setNewMutexGroup("")
	}

	const removeMutexGroup = (name) => {
		setGlobalMutexGroups(GlobalMutexGroups.filter((group) => group !== name))
		setGlobalAnnotationGroupAssignments((current) => Object.fromEntries(
			Object.entries(current).filter(([, group]) => group !== name)
		))
	}

	const downloadTemplate = () => {
		const headers = ["Global Annotations", "Beat Types", "Interval Types"]
		const templateData = [
			headers.join(","),
			"annotation1,beatName1,intervalName1",
			"annotation2,beatName2,intervalName2"
		].join("\n")

		const blob = new Blob([templateData], { type: "text/csv;charset=utf-8;" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = "annotation_template.csv"
		link.click()
		URL.revokeObjectURL(link.href)
		toast.success("Template downloaded")
	}

	const handleBulkFileUpload = (event) => {
		const file = event.target.files?.[0]
		if (!file) return

		const fileName = file.name.toLowerCase()
		if (!fileName.endsWith('.csv')) {
			toast.error("Please upload a .csv file")
			event.target.value = ''
			return
		}

		const reader = new FileReader()
		reader.onload = (e) => {
			const content = e.target?.result
			if (typeof content !== 'string') {
				toast.error("Could not read file")
				return
			}

			const lines = content.split(/\r?\n/)
			if (lines.length < 2) {
				toast.error("CSV file must have a header row and at least one data row")
				event.target.value = ''
				return
			}

			// Skip header row, process data rows
			const dataLines = lines.slice(1).filter(line => line.trim() !== '')

			// Local sets for deduping across all rows (includes default types)
			const seenGlobal = new Set()
			const seenBeats = new Set([DEFAULT_BEAT.name.toLowerCase()])
			const seenIntervals = new Set([DEFAULT_INTERVAL.name.toLowerCase()])

			const newGlobal = []
			const newBeats = []
			const newIntervals = []

			dataLines.forEach(line => {
				// Parse CSV line - handle empty columns
				const parts = line.split(',')
				const globalCol = parts[0]?.trim() || ""
				const beatCol = parts[1]?.trim() || ""
				const intervalCol = parts[2]?.trim() || ""

				// Column 1: Global Annotations
				if (globalCol) {
					const lower = globalCol.toLowerCase()
					if (!seenGlobal.has(lower)) {
						seenGlobal.add(lower)
						newGlobal.push(globalCol)
					}
				}

				// Column 2: Beat Types
				if (beatCol) {
					const lower = beatCol.toLowerCase()
					if (!seenBeats.has(lower)) {
						seenBeats.add(lower)
						newBeats.push({ name: beatCol, color: DEFAULT_BEAT.color, slug: beatCol[0]?.toLowerCase() || "" })
					}
				}

				// Column 3: Interval Types
				if (intervalCol) {
					const lower = intervalCol.toLowerCase()
					if (!seenIntervals.has(lower)) {
						seenIntervals.add(lower)
						newIntervals.push({ name: intervalCol, color: DEFAULT_INTERVAL.color, slug: intervalCol[0]?.toLowerCase() || "" })
					}
				}
			})

			// Dedup against existing state and default types ("normal", "noise") and append
			setGlobalAnn(prev => {
				const existing = new Set(prev.map(a => a.toLowerCase()))
				const toAdd = newGlobal.filter(a => !existing.has(a.toLowerCase()))
				return [...prev, ...toAdd]
			})
			setBeats(prev => {
				const existing = new Set([...prev.map(b => b.name.toLowerCase()), "normal"])
				const toAdd = newBeats.filter(b => !existing.has(b.name.toLowerCase()))
				return [...prev, ...toAdd]
			})
			setIntervals(prev => {
				const existing = new Set([...prev.map(i => i.name.toLowerCase()), "noise"])
				const toAdd = newIntervals.filter(i => !existing.has(i.name.toLowerCase()))
				return [...prev, ...toAdd]
			})

			const globalAdded = newGlobal.length
			const beatAdded = newBeats.length
			const intervalAdded = newIntervals.length

			const messages = []
			if (globalAdded > 0) {
				messages.push(`Global: ${globalAdded} added`)
			}
			if (beatAdded > 0) {
				messages.push(`Beats: ${beatAdded} added`)
			}
			if (intervalAdded > 0) {
				messages.push(`Intervals: ${intervalAdded} added`)
			}
			if (messages.length > 0) {
				toast.success(messages.join(' | '))
			} else {
				toast.error("No valid data found in file")
			}
			event.target.value = ''
		}
		reader.onerror = () => {
			toast.error("Error reading file")
			event.target.value = ''
		}
		reader.readAsText(file)
	}


	const addBeat = (e) => {
		if (NewBeat.name === "") {
			toast.error("Beat name cannot be empty")
			return
		}
		if (NewBeat.color === "") {
			toast.error("Beat color cannot be empty")
			return
		}

		const existing = new Set([...Beats.map(b => b.name.toLowerCase()), DEFAULT_BEAT.name.toLowerCase()])
		if (existing.has(NewBeat.name.toLowerCase())) {
			toast.error("Beat name already exists")
			return
		}

		const newBeats = [...Beats, {...NewBeat, slug: NewBeat.name.toLowerCase()[0]}]
		setBeats(newBeats)
		toast.success("Added Beat")
		setNewBeat({name: "", color: "", slug: ""})
	}

	const addInterval = (e) => {
		if (NewInterval.name === "") {
			toast.error("Interval name cannot be empty")
			return
		}
		if (NewInterval.color === "") {
			toast.error("Interval color cannot be empty")
			return
		}

		const existing = new Set([...Intervals.map(i => i.name.toLowerCase()), DEFAULT_INTERVAL.name.toLowerCase()])
		if (existing.has(NewInterval.name.toLowerCase())) {
			toast.error("Interval name already exists")
			return
		}

		const newIntervals = [...Intervals, {...NewInterval, slug: NewInterval.name.toLowerCase()[0]}]
		setIntervals(newIntervals)
		toast.success("Added Interval")
		setNewInterval({name: "", color: "", slug: ""})
	}

	const axiosPrivate = useAxiosPrivate()

    useEffect(() => {
        window.sidebar.open()
    }, [])

	// write a funtion that parses the jwt token in window.localStorage.getItem("usr")).token and returns the expiry date and time as string

	const jwtExpiry = () => {
		const token = JSON.parse(window.localStorage.getItem("usr")).token
		const base64Url = token.split(".")[1]
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
		const jsonPayload = decodeURIComponent(
			atob(base64)

				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
				})
				.join("")
		)
	
		const jwt = JSON.parse(jsonPayload)
		const iat = new Date(jwt.iat * 1000)
		const expiry = new Date(jwt.exp * 1000)
		return (
			<>
				Token issued at: <b>{iat.toString()}</b> <br/> expires at: <b>{expiry.toString()}</b>
			</>
		)
	}

	const onClickCancelUpload = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}
	}

	const onClickUpload = async () => {
		if (TaskName === "") {
			toast.error("Task Name cannot be empty")
			return
		}
		if (Object.keys(Files).length === 0) {
			toast.error("Please select at least one recording")
			return
		}
		if (TaskType !== 2 && RequireGlobalAnnotation && GlobalAnn.length === 0) {
			toast.error("Add at least one global annotation before requiring a selection")
			return
		}
		setLoading(true)
		let annotation = {
			global: [],
			taskInstructions: TaskInstructions.trim(),
			labels: {beats: [], intervals: []},
			annotationFeatures: {
				beatAnnotation: TaskType === 2 || !DisableBeatAnnotation,
				intervalAnnotation: TaskType === 2 || !DisableIntervalAnnotation,
				medianBeatFiducialPointAnnotation: TaskType === 2 || !DisableMedianBeatFiducialPointAnnotation,
				jsonView: TaskType === 2 || !HideJsonView,
				rmsView: TaskType === 2 || !DisableRmsView,
				algorithmAnalysisView: TaskType === 2 || !HideAlgorithmAnalysis,
				doctorNoteRequired: TaskType !== 2 && RequireDoctorNote,
			},
			globalAnnotationRules: {
				requireAtLeastOne: TaskType !== 2 && RequireGlobalAnnotation,
				mutexEnabled: TaskType !== 2 && EnableGlobalAnnotationMutex,
				mutexGroups: GlobalMutexGroups.map((name) => ({
					name,
					annotations: GlobalAnn.filter((label) => GlobalAnnotationGroupAssignments[label] === name),
				})),
			},
		}
		// Global Annotations
		GlobalAnn.map(g => {
			annotation.global.push({[g]: false})
		})

		// Beat Annotations
		annotation.labels.beats = [DEFAULT_BEAT,...Beats]

		// Interval Annotations
		annotation.labels.intervals = [DEFAULT_INTERVAL,...Intervals]

		const fileList = Object.values(Files)
		const total = fileList.length
		const controller = new AbortController()
		abortControllerRef.current = controller

		let taskId
		try {
			const { data: postData } = await axiosPrivate[METHODS.POST.toLowerCase()](`task`, {
				"name": TaskName,
				"taskType": "ekg",
				"recordings": []
			}, { signal: controller.signal })
			taskId = postData.task_id
		} catch (error) {
			if (typeof error === "string") {
				toast.error(error)
			} else {
				toast.error("Error creating Task")
			}
			setError(errorHandler(error))
			setLoading(false)
			abortControllerRef.current = null
			return
		}

		// Pre-process all files into uniEcg objects with annotations
		const recordings = fileList.map((file) => {
			let uniEcg = JSON.parse(file)
			// Preserve any pre-existing beat annotations on the incoming file
			// (turk-style JSONs from Turk V1 arrive with `annotations.beatLabels`
			// and/or `annotations.beatsV2` populated). Task-level annotation config
			// only supplies `global` + `labels`, so merging keeps existing beats.
			const existingAnnotations = uniEcg.annotations || {}
			if (TaskType == 2 && uniEcg.hasOwnProperty("kai12Analysis")) {
				uniEcg.annotations = {
					...existingAnnotations,
					...annotation,
					overreadMode: true,
					originalDx: uniEcg.kai12Analysis,
					confirmedDx: null,
				}
			} else {
				uniEcg.annotations = {
					...existingAnnotations,
					...annotation,
				}
			}
			return uniEcg
		})

		// Upload in batches of up to 500 recordings per POST /recordings call
		const BATCH_SIZE = 100
		let completed = 0
		let failed = 0

		try {
			for (let i = 0; i < recordings.length; i += BATCH_SIZE) {
				if (controller.signal.aborted) break

				const batch = recordings.slice(i, i + BATCH_SIZE)
				try {
					await axiosPrivate[METHODS.POST.toLowerCase()](`recordings`, {
						task_id: taskId,
						recordings: batch,
					}, { signal: controller.signal })
					completed += batch.length
				} catch (err) {
					// If batch fails, count all in the batch as failed and continue with next batch
					failed += batch.length
				}
				setUploadProgress({ total, completed, failed })
			}
		} catch (error) {
			if (!controller.signal.aborted) {
				toast.error("Upload failed")
				setError(errorHandler(error))
			}
		}

		abortControllerRef.current = null
		setLoading(false)

		if (controller.signal.aborted) {
			toast(`Upload cancelled: ${completed} of ${total} recordings uploaded. Re-upload is safe — already-uploaded files will be skipped.`)
		} else if (failed === 0) {
			toast.success(`Task created with ${completed} recording${completed === 1 ? '' : 's'}`)
			setTaskName("")
			setTaskInstructions("")
			setFiles({})
			setDisableBeatAnnotation(false)
			setDisableIntervalAnnotation(false)
			setDisableMedianBeatFiducialPointAnnotation(false)
			setHideJsonView(false)
			setDisableRmsView(false)
			setHideAlgorithmAnalysis(false)
			setRequireDoctorNote(false)
			setRequireGlobalAnnotation(false)
			setEnableGlobalAnnotationMutex(false)
			setGlobalMutexGroups([])
			setNewMutexGroup("")
			setGlobalAnnotationGroupAssignments({})
		} else {
			toast(`Task created: ${completed} of ${total} recordings uploaded, ${failed} failed`)
		}
		setUploadProgress({ total: 0, completed: 0, failed: 0 })
	}

	const onDrop = useCallback((acceptedFiles) => {
		setLoading(true)
		let success = 0
		let failed = 0
		if (acceptedFiles.length > 0) {
			try {
				acceptedFiles.map((file) => {
					const reader = new FileReader()
					reader.onload = () => {
						const fileAsBinaryString = reader.result
						switch (file.name.split(".").pop()) {
							case "atc":
								const readingFile = new Promise((resolve, reject) => {
									const reader = new FileReader()
									reader.onloadend = (e) => resolve(e.target.result)
									reader.onerror = (e) => reject(e.target.error)
									reader.readAsArrayBuffer(file)
								}).then((FileBuffer) => {
									const data = new Uint8Array(FileBuffer)
									const { parsed_data, error_msg } = atc2json.parse(data)
									let uniEcg = JSON.parse(JSON.stringify(parsed_data))
									uniEcg.type = "atc"
									uniEcg.filename = file.name
									uniEcg.annotator = [{time: new Date().toISOString(),person: CurrentUserEmail ,message: "File Uploaded"}]
									// uniEcg.annotations = { beatLabels: [], intervals: [], global: [] }
									setFiles((prev) => ({ ...prev, [file.name]: JSON.stringify(uniEcg) }))
									success++
									// toast.success(`${file.name.length > 10 ? file.name.substring(0, 20) + "(...)." + file.name.split(".").pop() : file.name} loaded successfully`)
									if (error_msg) {
										toast.error(error_msg)
										setLoading(false)
										failed++
									}
								})
								break
							case "json":
								try {
									// A file saved from "Download JSON" is the whole
									// `GET /recording` response (`{id, data: {...}}`), not the
									// bare ECG. Unwrap before the sniff reads the shape, or it
									// mis-types the file as `turk` and the recording uploads
									// fine but cannot be opened.
									let uniEcg = unwrapRecordingEnvelope(JSON.parse(fileAsBinaryString))
									// Delegate to the shared sniff so re-uploaded task-download JSONs
									// (including ATC-origin `.atc.json` files) route to the correct
									// universalFormatter branch. resolveEcgType checks the stored
									// `type` first, then falls back to shape detection.
									uniEcg.type = resolveEcgType(uniEcg, uniEcg.type)
									uniEcg.filename = file.name
									uniEcg.annotator = [{time: new Date().toISOString(),person: CurrentUserEmail ,message: "File Uploaded"}]
									// uniEcg.annotations = { beatLabels: [], intervals: [], global: [] }
									setFiles((prev) => ({ ...prev, [file.name]: JSON.stringify(uniEcg) }))
									success++
									// toast.success(`${file.name.length > 10 ? file.name.substring(0, 20) + "(...)." + file.name.split(".").pop() : file.name} loaded successfully`)
								} catch (error) {
									failed++
									console.error(`[UploadTask] failed to parse ${file.name}`, error)
									toast.error(`${file.name.length > 10 ? file.name.substring(0, 20) + "(...)." + file.name.split(".").pop() : file.name} is not a valid ECG JSON file`)
								}
								break
							default:
								failed++
								toast.error("Invalid file type")
								setLoading(false)
						}
					}
					reader.onabort = () => console.log("file reading was aborted")
					reader.onerror = () => console.log("file reading has failed")
					reader.readAsBinaryString(file)
				})
			} catch (error) {
				if (typeof error === "string") {
					toast.error(error)
				} else {
					toast.error("Error fetching Task Recordings")
				}
				console.log(error)
				setError(error)
			} finally {
				setLoading(false)
			}
			setTimeout(() => {
				if ( acceptedFiles.length === success + failed) {
					if(acceptedFiles.length === success) {
						toast.success(`${success} files loaded successfully`)
					} else {
						toast(`Of the ${acceptedFiles.length} files, ${success} loaded successfully and ${failed} failed`)
					}
				}
			},  acceptedFiles.length*100)
		} else {
			setLoading(false)
			toast.error("No ECG file selected")
		}
	}, [])

	const { getRootProps, getInputProps, isDragActive, isDragReject } =
		useDropzone({
			accept: {
				"application/atc": [".atc"],
				"application/json": [".json"],
			},
			disabled,
			onDrop,
		})

	return (
		<Layout.CenterWrapper>
			{loading || error?.status === 421 ? (
				<Loader />
			) : error ? (
				<PlaceholderText>Error</PlaceholderText>
			) : (
				<div className={styles.root}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h5" component="div">
									Step 1: <b>Task Details</b>
								</Typography>
							</Grid>
							<Grid item xs={9}>
								<Card sx={{ padding: theme.spacing(3)}}>
									<TextField
										id="task_name"
										label="Task Name"
										variant="outlined"
										value={TaskName}
										onChange={(e) => {
											setTaskName(e.target.value)
										}}
										sx={{ whiteSpace: "nowrap"}}
										fullWidth
										size="large"
									/>
								</Card>
							</Grid>
							<Grid item xs={3}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<FormControl fullWidth sx={{ whiteSpace: "nowrap"}} size="large">
										<InputLabel id="task-type-label">Task Type</InputLabel>
										<Select
											labelId="task-type-label"
											id="task-type"
											label="Task Type"
											value={TaskType}
											onChange={(e) => setTaskType(e.target.value)}
											name="adaptive"
										>
											<MenuItem value={1}>ECG</MenuItem>
											<MenuItem value={2}>12L ECG Overread</MenuItem>
										</Select>
									</FormControl>
								</Card>
							</Grid>
							<Grid item xs={12}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<TextField
										id="task_instructions"
										label="Instructions for annotators (optional)"
										value={TaskInstructions}
										onChange={(e) => setTaskInstructions(e.target.value)}
										multiline
										minRows={4}
										fullWidth
										helperText="These instructions will appear in an Instructions tab while annotators review each recording."
									/>
								</Card>
							</Grid>
							<Grid item xs={12} mt={2}>
								<Typography variant="h5" component="div">
									Step 2: <b>Recordings</b>
								</Typography>
								<Typography variant="p" component="div">
									This tool currently supports ATC and 12L JSON Files only.
								</Typography>
							</Grid>
							<Grid item xs={8}>
								<Card sx={{ padding: theme.spacing(1), height:"50vh"}}>
									<div className={styles.uploadContainer} {...getRootProps()}>
										<input className={styles.inputBox} {...getInputProps()} />
										{!isDragActive && !isDragReject && (
											<h3 style={{ fontWeight: "500" }}>
												Click or Drag & drop ECG files here
											</h3>
										)}
										{isDragActive && !isDragReject && (
											<h3 style={{ fontWeight: "500" }}>Drop the file to parse...</h3>
										)}
										{isDragReject && (
											<h3 style={{ fontWeight: "500" }}>Drop the file to parse...</h3>
										)}
									</div>
								</Card>
							</Grid>
							<Grid item xs={4}>
								<Card sx={{ padding: theme.spacing(3), height:"50vh", overflowY: "scroll"}}>
									<Typography variant="h5" mb={2} component="div">
										<b>Recordings:</b>
									</Typography>
									<div style={{width:"100%",}}>
										{Object.keys(Files) && Object.keys(Files).map((file, index) => (
											<Chip
												key={index}
												label={file}
												color="primary"
												onDelete={() => {
													const newFiles = { ...Files }
													delete newFiles[file]
													setFiles(newFiles)
												}}
												style={{ margin: "3px" }}
											/>

										))}
									</div>

									<Button
										variant="contained"
										sx={{width: "100%", marginTop:"10px"}}
										size="large"
										color="error"
										onClick={() => {
											toast.success("All files cleared")
											setFiles({})
										}}
									>
										Clear All files
									</Button>
								</Card>
							</Grid>

							<Grid item xs={12} mt={2}>
								<Typography variant="h5" component="div">
									Step 3: <b>Annotation Options</b>
								</Typography>
							</Grid>

							{TaskType !== 2 && <Grid item xs={12} mt={1}>
								<Card sx={{ padding: theme.spacing(2) }}>
									<Typography variant="h6" component="div" sx={{ mb: 1 }}>
										<b>Simplified UI:</b>
									</Typography>
									<FormGroup row>
										<FormControlLabel
											control={
												<Switch
													checked={DisableBeatAnnotation}
													onChange={(e) => setDisableBeatAnnotation(e.target.checked)}
													name="disableBeatAnnotation"
												/>
											}
											label="Disable beat annotation"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={DisableIntervalAnnotation}
													onChange={(e) => setDisableIntervalAnnotation(e.target.checked)}
													name="disableIntervalAnnotation"
												/>
											}
											label="Disable interval annotation"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={DisableMedianBeatFiducialPointAnnotation}
													onChange={(e) => setDisableMedianBeatFiducialPointAnnotation(e.target.checked)}
													name="disableMedianBeatFiducialPointAnnotation"
												/>
											}
											label="Disable median beat fiducial point annotation"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={HideJsonView}
													onChange={(e) => setHideJsonView(e.target.checked)}
													name="hideJsonView"
												/>
											}
											label="Hide JSON view"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={DisableRmsView}
													onChange={(e) => setDisableRmsView(e.target.checked)}
													name="disableRmsView"
												/>
											}
											label="Disable RMS view"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={HideAlgorithmAnalysis}
													onChange={(e) => setHideAlgorithmAnalysis(e.target.checked)}
													name="hideAlgorithmAnalysis"
												/>
											}
											label="Hide automated analysis and measurements"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={RequireGlobalAnnotation}
													onChange={(e) => setRequireGlobalAnnotation(e.target.checked)}
													name="requireGlobalAnnotation"
												/>
											}
											label="Require at least one global annotation"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={RequireDoctorNote}
													onChange={(e) => setRequireDoctorNote(e.target.checked)}
													name="requireDoctorNote"
												/>
											}
											label="Require Doctor Note before saving"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={EnableGlobalAnnotationMutex}
													onChange={(e) => setEnableGlobalAnnotationMutex(e.target.checked)}
													name="enableGlobalAnnotationMutex"
												/>
											}
											label="Enable global annotation mutex groups"
										/>
									</FormGroup>
									<FormHelperText>
										Disabled annotation tools and views will be hidden and unavailable to annotators for this task.
									</FormHelperText>
								</Card>
							</Grid>}

							<Grid item xs={12} mt={1}>
								<Card sx={{ padding: theme.spacing(2) }}>
									<Typography variant="h6" component="div" sx={{ mb: 1 }}>
										<b>Bulk Upload Annotation Labels from CSV:</b>
									</Typography>
									<input
										type="file"
										accept=".csv"
										hidden
										ref={fileInputRef}
										onChange={handleBulkFileUpload}
									/>
									<Button
										variant="contained"
										size="small"
										onClick={() => fileInputRef.current?.click()}
										sx={{ mr: 1 }}
									>
										Upload CSV
									</Button>
									<Button
										variant="outlined"
										size="small"
										onClick={downloadTemplate}
									>
										Download Template
									</Button>
									<Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
										One label per line for each column: Global Annotations | Beat Types | Interval Types
									</Typography>
								</Card>
							</Grid>

							{TaskType !== 2 && <Grid item xs={4} mt={2}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<Typography variant="h6" component="div">
										<b>Global Annotations:</b>
									</Typography>

									<div style={{width:"100%", height: "15vh", overflow: "scroll", padding: "10px 0px"}}>
										{GlobalAnn && GlobalAnn.map((Ga, index) => (
											<Chip
												key={index}
												label={Ga}
												color="primary"
												onDelete={() => {
													const newGlobalAnn = GlobalAnn.filter((item) => item !== Ga)
													setGlobalAnn(newGlobalAnn)
													setGlobalAnnotationGroupAssignments((current) => {
														const updated = { ...current }
														delete updated[Ga]
														return updated
													})
												}}
												style={{ margin: "3px" }}
											/>
										))}
									</div>
									<Grid spacing={1} container>
										<Grid item xs={7.5}>
											<TextField
												label="Global Annotation"
												variant="outlined"
												value={NewGlobalAnn}
												onChange={(e) => {
													setNewGlobalAnn(e.target.value)
												}}
												fullWidth
												size="small"
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														addGlobalAnn()
												}}}
											/>
										</Grid>
										<Grid item xs={2} mr={1}>
											<Button
												variant="contained"
												color="error"
												onClick={() => {
													setGlobalAnn([])
													setGlobalAnnotationGroupAssignments({})
												}}
											>
												<BackspaceIcon />
											</Button>
										</Grid>
										<Grid item xs={2}>
											<Button
												variant="contained"
												onClick={addGlobalAnn}
											>
												<AddIcon />
											</Button>
										</Grid>
									</Grid>
									
								</Card>
							</Grid>}

							{TaskType !== 2 && EnableGlobalAnnotationMutex && <Grid item xs={12} mt={2}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<Typography variant="h6" component="div">
										<b>Global Annotation Mutex Groups:</b>
									</Typography>
									<FormHelperText sx={{ mb: 2 }}>
										Annotations in the same group may be selected together. Selecting from different groups is not allowed. Unassigned annotations are compatible with every group.
									</FormHelperText>
									<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
										<TextField
											label="New mutex group"
											value={NewMutexGroup}
											onChange={(e) => setNewMutexGroup(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") addMutexGroup()
											}}
											size="small"
										/>
										<Button variant="contained" onClick={addMutexGroup}>Add group</Button>
									</Box>
									<Box sx={{ mb: 2 }}>
										{GlobalMutexGroups.map((group) => (
											<Chip key={group} label={group} onDelete={() => removeMutexGroup(group)} sx={{ mr: 1, mb: 1 }} />
										))}
									</Box>
									<Grid container spacing={2}>
										{GlobalAnn.map((annotationName) => (
											<React.Fragment key={annotationName}>
												<Grid item xs={6}><Typography>{annotationName}</Typography></Grid>
												<Grid item xs={6}>
													<FormControl fullWidth size="small">
														<Select
															value={GlobalAnnotationGroupAssignments[annotationName] || ""}
															onChange={(e) => setGlobalAnnotationGroupAssignments({
																...GlobalAnnotationGroupAssignments,
																[annotationName]: e.target.value,
															})}
															displayEmpty
														>
															<MenuItem value=""><em>Unassigned</em></MenuItem>
															{GlobalMutexGroups.map((group) => <MenuItem key={group} value={group}>{group}</MenuItem>)}
														</Select>
													</FormControl>
												</Grid>
											</React.Fragment>
										))}
									</Grid>
								</Card>
							</Grid>}

							{!DisableBeatAnnotation && <Grid item xs={4} mt={2}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<Typography variant="h6" component="div">
										<b>Beat Types:</b>
									</Typography>

									<div style={{width:"100%", height: "15vh", overflow: "scroll", padding: "10px 0px"}}>
										<Chip
											key={"exisitng"}
											label={DEFAULT_BEAT.name}
											color="primary"
											style={{ margin: "3px", backgroundColor: DEFAULT_BEAT.color }}
										/>
										{Beats && Beats.map((b, index) => (
											<Chip
												key={index}
												label={b.name}
												color="primary"
												onDelete={() => {
													const newBeats = Beats.filter((item) => item !== b)
													setBeats(newBeats)
												}}
												style={{ margin: "3px", backgroundColor: b.color }}
											/>
										))}
									</div>
									<Grid spacing={1} container>
										<Grid item xs={4}>
											<TextField
												label="Name"
												variant="outlined"
												value={NewBeat.name}
												onChange={(e) => {
													setNewBeat({...NewBeat, name: e.target.value})
												}}
												size="small"
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														addBeat()
												}}}
											/>
										</Grid>
										<Grid item xs={5.5}>
											<MuiColorInput
												label="Color"
												variant="outlined"
												value={NewBeat.color}
												onChange={color => { setNewBeat({...NewBeat, color: color}) }}
												size="small"
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														addBeat()
												}}}
											/>
										</Grid>
										<Grid item xs={2}>
											<Button
												variant="contained"
												fullWidth
												onClick={addBeat}
											>
												<AddIcon />
											</Button>
										</Grid>
									</Grid>
								</Card>
							</Grid>}

							{!DisableIntervalAnnotation && <Grid item xs={4} mt={2}>
								<Card sx={{ padding: theme.spacing(3) }}>
									<Typography variant="h6" component="div">
										<b>Interval Types:</b>
									</Typography>

									<div style={{width:"100%", height: "15vh", overflow: "scroll", padding: "10px 0px"}}>
										<Chip
											key={"exisitng"}
											label={DEFAULT_INTERVAL.name}
											color="primary"
											style={{ margin: "3px", backgroundColor: DEFAULT_INTERVAL.color }}
										/>
										{Intervals && Intervals.map((b, index) => (
											<Chip
												key={index}
												label={b.name}
												color="primary"
												onDelete={() => {
													const newIntervals = Intervals.filter((item) => item !== b)
													setIntervals(newIntervals)
												}}
												style={{ margin: "3px", backgroundColor: b.color }}
											/>
										))}
									</div>
									<Grid spacing={1} container>
										<Grid item xs={4}>
											<TextField
												label="Name"
												variant="outlined"
												value={NewInterval.name}
												onChange={(e) => {
													setNewInterval({...NewInterval, name: e.target.value})
												}}
												size="small"
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														addInterval()
												}}}
											/>
										</Grid>
										<Grid item xs={5.5}>
											<MuiColorInput
												label="Color"
												variant="outlined"
												value={NewInterval.color}
												onChange={color => { setNewInterval({...NewInterval, color: color}) }}
												size="small"
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														addInterval()
												}}}
											/>
										</Grid>
										<Grid item xs={2}>
											<Button
												variant="contained"
												fullWidth
												onClick={addInterval}
											>
												<AddIcon />
											</Button>
										</Grid>
									</Grid>
								</Card>
							</Grid>}
							

							<Grid item xs={12} mt={2}>
								<Typography variant="h5" component="div">
									Step 4: <b>Upload</b>
								</Typography>
							</Grid>
							<Grid item xs={4}>
								<Card sx={{ padding: theme.spacing(2) }}>
								<Typography variant="p" pb={2} component="div">
									Uploading <b>{TaskName || "Untitled"}</b> task with <b>{Object.keys(Files).length}</b> recordings.
								</Typography>
								{uploadProgress.total > 0 ? (
									<>
										<Typography variant="body2" sx={{ mb: 1 }}>
											Uploading {uploadProgress.completed + uploadProgress.failed} / {uploadProgress.total}
											{uploadProgress.failed > 0 ? ` (${uploadProgress.failed} failed)` : ''}
										</Typography>
										<LinearProgress
											variant="determinate"
											value={((uploadProgress.completed + uploadProgress.failed) / uploadProgress.total) * 100}
											sx={{ mb: 2 }}
										/>
										<Button
											variant="outlined"
											color="error"
											sx={{width: "100%"}}
											size="large"
											onClick={onClickCancelUpload}
										>
											Cancel
										</Button>
									</>
								) : (
									<Button
										variant="contained"
										sx={{width: "100%"}}
										size="large"
										onClick={onClickUpload}
										disabled={loading}
									>
										Upload
									</Button>
								)}
								</Card>
							</Grid>
							<Grid item xs={12} my={4} textAlign="center">
								<p>For bulk upload with <Link href="/bulk_upload_script.py">python script</Link> from local, <Link href="#" onClick={()=>{copy2cliboard(JSON.parse(window.localStorage.getItem("usr")).token); toast.success("Bearer Token Copied!")}}>Click here</Link> to copy bearer token.</p>
								<p>{jwtExpiry()}</p>
							</Grid>
						</Grid>
				</div>
			)}
		</Layout.CenterWrapper>
	)
}

export default UploadTask
